import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { MatchTypes } from '@core/interfaces';

import { ItemService } from '@item/services';

@Component({
  selector: 'z-item-lookup',
  templateUrl: './item-lookup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class ItemLookupComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  @Input() fieldName: string;

  @Input() custid: string;

  @Input() label: string;
  @Input() placeholder: string;
  @Input() hint: string;

  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() uppercase: boolean = true;

  @Input() clearButton: boolean = true;
  @Input() manualInput: boolean = true;

  @Input() enableDropdown: boolean = true;
  @Input() showDropdownFilter: boolean = true;
  @Input() doubleClickToOpenPopup: boolean = true;

  @Input() matchTypes: boolean = false;
  @Input() defaultMatchType: MatchTypes = 'equals';

  @Output() matchTypeChange = new EventEmitter<any>();

  @Output() change = new EventEmitter<string>();
  @Output() clear = new EventEmitter<any>();

  useDefaultLabel: boolean = false;
  useDefaultPlaceholder: boolean = false;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(private translocoService: TranslocoService, private itemService: ItemService, public formGroup: FormGroupDirective) {}

  ngOnInit() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.label) {
          this.useDefaultLabel = true;
          this.label = this.translate('core.components.lookups.item_lookup.label');
        }

        if (!this.placeholder) {
          this.useDefaultPlaceholder = true;
          this.placeholder = this.translate('core.components.lookups.item_lookup.placeholder');
        }
      });

    this.translocoService.events$
      .pipe(
        filter((event) => event.type === 'langChanged'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onLanguageChange();
      });

    this.itemService.requestLists();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  onLanguageChange() {
    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe(() => {
        if (this.useDefaultLabel) {
          this.label = this.translate('core.components.lookups.item_lookup.label');
        }

        if (this.useDefaultPlaceholder) {
          this.placeholder = this.translate('core.components.lookups.item_lookup.placeholder');
        }
      });
  }

  openDialog = () => {
    const lookupDialogRef = this.itemService.itemLookupDialog(this.custid);

    lookupDialogRef.onClose.pipe(take(1)).subscribe((result) => {
      if (result !== undefined) {
        this.formGroup.form.get(this.fieldName).setValue(result.item);
      }
    });
  };

  onChangeHandler(value: string) {
    this.change.emit(value);
  }

  onClearHandler(_event: any) {
    this.clear.emit();
  }

  onMatchTypeChange = (event: any) => {
    this.matchTypeChange.emit(event);
  };
}
